import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Bell, 
  BellRing, 
  Check, 
  CheckCircle2, 
  Trash2, 
  Filter, 
  X, 
  AlertTriangle,
  MessageSquare,
  Plus,
  UserCheck,
  Forward,
  RefreshCw,
  Settings,
  Clock,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import notificationService from '../../services/notificationService';

// Define icon mapping at module level to avoid conflicts
const getIconComponent = (iconName) => {
  const icons = {
    Plus, 
    UserCheck, 
    AlertTriangle, 
    Forward, 
    CheckCircle2, 
    MessageSquare, 
    RefreshCw, 
    Settings, 
    Clock, 
    Info, 
    Bell
  };
  return icons[iconName] || Bell;
};

const Notifications = () => {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications', { page: currentPage, types: selectedTypes }],
    queryFn: () => notificationService.getNotifications({ 
      page: currentPage,
      types: selectedTypes.length > 0 ? selectedTypes : undefined
    })
  });

  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const markAsReadMutation = useMutation({
    mutationFn: notificationService.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-unread-count']);
      
      // Dispatch event to update other components
      window.dispatchEvent(new CustomEvent('notificationUpdate', {
        detail: { type: 'notification_read' }
      }));
    },
    onError: (error) => toast.error(error.message)
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: notificationService.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-unread-count']);
      toast.success('All notifications marked as read');
      
      // Dispatch event to update other components
      window.dispatchEvent(new CustomEvent('notificationUpdate', {
        detail: { type: 'all_read', count: 0 }
      }));
    },
    onError: (error) => toast.error(error.message)
  });

  const deleteMutation = useMutation({
    mutationFn: notificationService.deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notifications-unread-count']);
      toast.success('Notification deleted');
      
      // Dispatch event to update other components
      window.dispatchEvent(new CustomEvent('notificationUpdate', {
        detail: { type: 'notification_read' }
      }));
    },
    onError: (error) => toast.error(error.message)
  });

  const notifications = notificationsData?.data?.notifications || [];
  const pagination = notificationsData?.data?.pagination;
  const unreadCount = unreadCountData?.data?.unread_count || 0;
  const notificationTypes = notificationService.getNotificationTypes();

  const groupedNotifications = notificationService.groupNotificationsByDate(notifications);

  const handleMarkAsRead = (notificationId) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleDelete = (notificationId) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      deleteMutation.mutate(notificationId);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (!notification.read_at) {
      markAsReadMutation.mutate(notification.id);
    }

    // Navigate to action URL if available
    const actionUrl = notificationService.getNotificationActionUrl(notification);
    if (actionUrl) {
      window.location.href = actionUrl;
    }
  };

  const handleTypeFilter = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BellRing className="w-6 h-6 mr-2" />
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-gray-600 mt-1">Stay updated on ticket activities and system events</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
            {selectedTypes.length > 0 && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                {selectedTypes.length}
              </span>
            )}
          </button>
          
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isLoading}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              Mark All Read
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Filter by Type</h3>
          <div className="flex flex-wrap gap-2">
            {notificationTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => handleTypeFilter(type.value)}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  selectedTypes.includes(type.value)
                    ? `bg-${type.color}-100 text-${type.color}-800 border-${type.color}-200`
                    : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
          
          {selectedTypes.length > 0 && (
            <button
              onClick={() => setSelectedTypes([])}
              className="mt-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Notifications List */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
            <p className="text-gray-500">
              {selectedTypes.length > 0 
                ? 'No notifications match your current filters.'
                : 'You\'re all caught up! New notifications will appear here.'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {Object.entries(groupedNotifications).map(([dateGroup, groupNotifications]) => (
              <div key={dateGroup}>
                {/* Date Header */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900">{dateGroup}</h3>
                </div>
                
                {/* Notifications in this date group */}
                {groupNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDelete={handleDelete}
                    onClick={handleNotificationClick}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
              {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
              {pagination.total} notifications
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={pagination.current_page === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-700">
                Page {pagination.current_page} of {pagination.total_pages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
                disabled={pagination.current_page === pagination.total_pages}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NotificationItem = ({ notification, onMarkAsRead, onDelete, onClick }) => {
  const isUnread = !notification.read_at;
  const priority = notificationService.getNotificationPriority(notification);
  const message = notificationService.formatNotificationMessage(notification);
  const timeAgo = notificationService.formatNotificationTime(notification.created_at);
  const isActionable = notificationService.isActionable(notification);
  const color = notificationService.getNotificationColor(notification.type);
  const iconName = notificationService.getNotificationIcon(notification.type);
  const IconComponent = getIconComponent(iconName); // Use the module-level function

  const priorityStyles = {
    high: 'border-l-red-500 bg-red-50',
    medium: 'border-l-orange-500 bg-orange-50',
    normal: isUnread ? 'border-l-blue-500 bg-blue-50' : 'border-l-gray-300'
  };

  const colorStyles = {
    blue: 'text-blue-600 bg-blue-100',
    indigo: 'text-indigo-600 bg-indigo-100',
    orange: 'text-orange-600 bg-orange-100',
    purple: 'text-purple-600 bg-purple-100',
    green: 'text-green-600 bg-green-100',
    red: 'text-red-600 bg-red-100',
    yellow: 'text-yellow-600 bg-yellow-100',
    gray: 'text-gray-600 bg-gray-100'
  };

  return (
    <div 
      className={`px-6 py-4 border-l-4 transition-colors hover:bg-gray-50 ${
        priorityStyles[priority]
      } ${isActionable ? 'cursor-pointer' : ''}`}
      onClick={() => isActionable && onClick(notification)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          {/* Icon */}
          <div className={`p-2 rounded-full ${colorStyles[color] || colorStyles.gray}`}>
            <IconComponent className="w-4 h-4" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className={`text-sm ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                {message}
              </p>
              {isUnread && (
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              )}
            </div>
            
            {/* Additional details from notification data */}
            {notification.data && typeof notification.data === 'object' && (
              <div className="text-sm text-gray-600 space-y-1">
                {notification.data.ticket_id && (
                  <div>Ticket #{notification.data.ticket_id}</div>
                )}
                {notification.data.priority && (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    notification.data.priority === 'high' 
                      ? 'bg-red-100 text-red-800'
                      : notification.data.priority === 'medium'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {notification.data.priority} priority
                  </span>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-4 mt-2">
              <span className="text-xs text-gray-500">{timeAgo}</span>
              {priority === 'high' && (
                <span className="text-xs font-medium text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  High Priority
                </span>
              )}
              {isActionable && (
                <span className="text-xs text-blue-600">Click to view</span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2 ml-4">
          {isUnread && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead(notification.id);
              }}
              className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
              title="Mark as read"
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(notification.id);
            }}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete notification"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notifications;